import {InputText} from '@/components/commons/input-text/InputText'
import {Switch} from '@/components/commons/switch/Switch'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {InfoCircleIcon} from '@/components/ui/icon/Icon'
import {Label} from '@/components/ui/label/Label'
import {Tooltip} from '@/components/ui/tooltip/Tooltip'
import {useTranslation} from '@/translations/i18n'
import {useEffect, useState} from 'react'
import {useTheme} from 'styled-components'

interface PlannedAccessTeamMeetingFieldsProps {
    onChange: (total: number) => void
    value?: number
    errorMessage?: string
    teamMembersLength?: number
}

const getDefaultBaseNumber = (value?: number, teamMembersLength?: number): number => {
    if (value && teamMembersLength && value % teamMembersLength === 0) {
        return value / teamMembersLength
    }
    return 0
}

const getDefaultIgnoreTeamMembersLength = (value?: number, teamMembersLength?: number): boolean => {
    if (value && teamMembersLength) {
        return value % teamMembersLength != 0 && value != 0
    }
    return teamMembersLength == 0
}

export const PlannedAccessTeamMeetingFields = ({
    errorMessage,
    value,
    onChange,
    teamMembersLength
}: PlannedAccessTeamMeetingFieldsProps) => {
    const {t} = useTranslation()
    const [baseNumber, setBaseNumber] = useState<number>(getDefaultBaseNumber(value, teamMembersLength))
    const [ignoreTeamMembersLength, setIgnoreTeamMembersLength] = useState<boolean>(
        getDefaultIgnoreTeamMembersLength(value, teamMembersLength)
    )

    const {palette} = useTheme()

    useEffect(() => {
        if (ignoreTeamMembersLength) {
            setBaseNumber(0)
        }
    }, [ignoreTeamMembersLength, onChange])

    return (
        <Flexbox gap={2.5}>
            <div style={{flexGrow: 0, width: '100px'}}>
                <InputText
                    label={t('commons:number')}
                    type="number"
                    value={baseNumber || ''}
                    onChange={event => {
                        setBaseNumber(event.target.value ? parseInt(event.target.value) : 0)
                        onChange(event.target.value ? parseInt(event.target.value) * (teamMembersLength || 0) : 0)
                    }}
                    placeholder="-"
                    errorMessage={errorMessage}
                    disabled={ignoreTeamMembersLength}
                    min={0}
                />
            </div>
            <Flexbox direction="column" gap={1.5}>
                <Flexbox gap={1}>
                    <Label>{t('commons:total')}</Label>
                    <Tooltip
                        variant="light"
                        content={t('planned_accesses:team_members_number', {number: teamMembersLength})}
                    >
                        <InfoCircleIcon color={palette.neutral[500]} size={14} />
                    </Tooltip>
                </Flexbox>
                <Flexbox gap={2.5} align="center">
                    <InputText
                        type="number"
                        value={value || ''}
                        onChange={event => onChange(event.target.value ? parseInt(event.target.value) : 0)}
                        placeholder="-"
                        min={0}
                        errorMessage={errorMessage}
                        disabled={!ignoreTeamMembersLength}
                        style={{width: '100px'}}
                    />
                    <Switch
                        size="sm"
                        label={t('planned_accesses:ignore_team_members')}
                        checked={ignoreTeamMembersLength}
                        disabled={teamMembersLength == 0}
                        onChange={() => {
                            setIgnoreTeamMembersLength(!ignoreTeamMembersLength)
                            onChange(0)
                        }}
                    />
                </Flexbox>
            </Flexbox>
        </Flexbox>
    )
}
